/**
 * Signed-in session timeline.
 *
 * `track()` calls {@link recordSessionEvent} for every product event.
 * The returned id is attached to the GA4 hit as `session_id`. When the
 * caller is signed in and {@link initSessionLog} has run, the same id
 * keys an append-only Firestore log:
 *
 *   users/{uid}/sessions/{sessionId}
 *   users/{uid}/sessions/{sessionId}/events/{autoId}
 *
 * A session rolls after 30 minutes of inactivity (GA4's window). The
 * parent doc is refreshed at most every 30 seconds. Writes stop after
 * {@link SESSION_MAX_EVENTS} so a runaway loop can't fill the collection.
 * `expireAt` is ~90 days out so a TTL policy on that field can delete
 * the docs. Signed-out and unconfigured calls still return a session
 * id (GA4 can use it) but do not touch Firestore.
 */

export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const SESSION_TOUCH_MS = 30 * 1000;
export const SESSION_MAX_EVENTS = 500;
export const SESSION_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;

export interface SessionWrite {
  uid: string;
  sessionId: string;
  startedAt: number;
  lastEventAt: number;
  eventCount: number;
  touchSession: boolean;
  release: string | null;
  expireAtMs: number;
  event: {
    t: number;
    name: string;
    props: Record<string, string | number | boolean | null>;
  };
}

type SessionState = {
  id: string;
  startedAt: number;
  lastEventAt: number;
  eventCount: number;
  lastDocWrite: number;
};

let loggingEnabled = false;
let session: SessionState | null = null;
let writerOverride: ((write: SessionWrite) => void) | null = null;

export function initSessionLog(): void {
  loggingEnabled = true;
}

export function recordSessionEvent(
  name: string,
  props: Record<string, unknown> | undefined,
  uid: string | null,
  now = Date.now()
): string {
  const current = ensureSession(now);
  if (!loggingEnabled || !uid) return current.id;
  if (current.eventCount >= SESSION_MAX_EVENTS) return current.id;

  current.eventCount += 1;
  const touchSession =
    current.lastDocWrite === 0 || now - current.lastDocWrite >= SESSION_TOUCH_MS;
  if (touchSession) current.lastDocWrite = now;

  const write: SessionWrite = {
    uid,
    sessionId: current.id,
    startedAt: current.startedAt,
    lastEventAt: now,
    eventCount: current.eventCount,
    touchSession,
    release: releaseTag(),
    expireAtMs: now + SESSION_RETENTION_MS,
    event: {
      t: now,
      name,
      props: scalarProps(props),
    },
  };

  if (writerOverride) writerOverride(write);
  else void persistSessionWrite(write).catch(() => {});
  return current.id;
}

function ensureSession(now: number): SessionState {
  if (!session || now - session.lastEventAt > SESSION_IDLE_MS) {
    session = {
      id: crypto.randomUUID(),
      startedAt: now,
      lastEventAt: now,
      eventCount: 0,
      lastDocWrite: 0,
    };
    return session;
  }
  session.lastEventAt = now;
  return session;
}

function releaseTag(): string | null {
  const v = import.meta.env.VITE_RELEASE;
  if (typeof v !== "string") return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 80) : null;
}

/**
 * Prop keys `firestore.rules` accepts on a session event. Anything else
 * is dropped here, because one unknown key rejects the whole write.
 * Must match the `hasOnly` list in `isValidActivityProps`.
 */
export const SESSION_PROP_KEYS: ReadonlySet<string> = new Set([
  "from", "to", "id", "shared", "is_new", "preset_name", "shape", "source",
  "mode", "updates", "additions", "removals", "reason", "room_id",
  "has_metered_price", "utm_source", "utm_medium", "utm_campaign",
  "utm_content", "utm_term", "template", "param", "material",
  "kind", "module", "action", "category", "method", "step",
  "steps_done", "in_app_browser", "seconds_since_invite", "surface",
  "first_touch_source", "first_touch_medium", "first_touch_campaign",
  "first_touch_content", "feature",
  "signed_in", "trigger", "value", "currency", "plan_interval",
  "trial_days", "activation_ms", "timed_out", "latency_ms",
  "credits_remaining", "score", "dimension_source", "plan",
  "choice", "limit", "flow", "seconds_since_start", "host",
  "reduced_motion", "providers", "stage", "page_path", "page_title",
  "dwell_ms", "phase", "lcp_ms", "inp_ms", "cls", "days",
  "load_ms", "backend", "quality", "duration_ms", "preset",
  "lighting_changes", "mode_switches", "walked", "own_room", "pass",
  "photos", "on", "completed", "active_ms", "walk_in", "walk_ms",
  "overview_ms", "looked", "quality_drops", "tour_ms",
  "type", "scope", "look", "setting", "color_name", "wall_key",
  "changes", "finish_changes",
  "image_count", "triangles", "bytes", "pbr",
  "input", "ms", "piece_px", "direction",
]);

function scalarProps(
  props: Record<string, unknown> | undefined
): Record<string, string | number | boolean | null> {
  if (!props) return {};
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(props)) {
    if (!SESSION_PROP_KEYS.has(key)) continue;
    if (value === null) {
      out[key] = null;
      continue;
    }
    if (typeof value === "string" || typeof value === "boolean") {
      out[key] = value;
      continue;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      out[key] = value;
    }
  }
  return out;
}

async function persistSessionWrite(write: SessionWrite): Promise<void> {
  const { isFirebaseConfigured, getFirebaseFirestore } = await import("./firebase");
  if (!isFirebaseConfigured()) return;
  const db = getFirebaseFirestore();
  if (!db) return;
  const { collection, doc, writeBatch, Timestamp } = await import("firebase/firestore");
  const sessionRef = doc(db, "users", write.uid, "sessions", write.sessionId);
  const eventRef = doc(collection(sessionRef, "events"));
  const expireAt = Timestamp.fromMillis(write.expireAtMs);
  const batch = writeBatch(db);
  if (write.touchSession) {
    batch.set(
      sessionRef,
      {
        startedAt: write.startedAt,
        lastEventAt: write.lastEventAt,
        eventCount: write.eventCount,
        release: write.release,
        expireAt,
      },
      { merge: true }
    );
  }
  batch.set(eventRef, {
    t: write.event.t,
    name: write.event.name,
    props: write.event.props,
    expireAt,
  });
  await batch.commit();
}

/** Test-only: replace the Firestore writer. Pass null to restore it. */
export function _setSessionWriterForTests(
  writer: ((write: SessionWrite) => void) | null
): void {
  writerOverride = writer;
}

/** Test-only: drop the in-memory session so the next event starts fresh. */
export function _resetSessionLogForTests(): void {
  loggingEnabled = false;
  session = null;
  writerOverride = null;
}
